/**
 * Google Maps for the Gravity Forms address lookup, loaded on demand.
 *
 * inc/form-performance.php prints the Maps API and the Geolocation add-on's
 * scripts as inert placeholders (type="text/plain", URL in
 * data-ih-deferred-src). Maps is about 390 KB, so it waits until the page has
 * loaded and then until either:
 *
 * - an address field comes within 200px of the screen, or
 * - someone focuses or presses anywhere inside a form.
 *
 * The scripts then load in their original order. The add-on sets up the
 * address lookup on gform/postRender, which has already fired for the forms on
 * the page, so it is fired again for each one. If loading fails, the address
 * fields still work by hand.
 */

const PLACEHOLDER = 'script[type="text/plain"][data-ih-deferred-src]';
const ADDRESS_FIELD = '.gfield[data-js="geolocation-enabled"]';

let loading = null;

function loadScript( placeholder ) {
	return new Promise( ( resolve, reject ) => {
		const script = document.createElement( 'script' );
		script.id = placeholder.id;
		script.src = placeholder.dataset.ihDeferredSrc;
		script.async = false;
		script.addEventListener( 'load', resolve, { once: true } );
		script.addEventListener( 'error', reject, { once: true } );
		placeholder.replaceWith( script );
	} );
}

function rerunFormSetup() {
	document.querySelectorAll( '.gform_wrapper form[data-formid]' ).forEach( ( form ) => {
		document.dispatchEvent(
			new CustomEvent( 'gform/postRender', {
				detail: {
					formId: Number( form.dataset.formid ),
					currentPage: Number( form.querySelector( '[name^="gform_source_page_number_"]' )?.value ) || 1,
				},
			} )
		);
	} );
}

function loadMaps() {
	if ( ! loading ) {
		const placeholders = Array.from( document.querySelectorAll( PLACEHOLDER ) );
		loading = placeholders
			.reduce( ( chain, placeholder ) => chain.then( () => loadScript( placeholder ) ), Promise.resolve() )
			// The add-on binds its gform/postRender listener on DOM ready; let that run first.
			.then( () => window.requestAnimationFrame( rerunFormSetup ) )
			.catch( () => {
				// The lookup is an enhancement: the address fields still take typed input.
			} );
	}

	return loading;
}

function watchForms() {
	const fields = document.querySelectorAll( ADDRESS_FIELD );
	const canObserve = 'IntersectionObserver' in window;

	if ( fields.length && ! canObserve ) {
		loadMaps();
		return;
	}

	const controller = new AbortController();
	const observer = fields.length && canObserve
		? new IntersectionObserver(
			( entries ) => {
				if ( entries.some( ( entry ) => entry.isIntersecting ) ) {
					start();
				}
			},
			{ rootMargin: '200px' }
		)
		: null;

	function start() {
		observer?.disconnect();
		controller.abort();
		loadMaps();
	}

	fields.forEach( ( field ) => observer?.observe( field ) );

	// Delegated, because multi-page forms replace their markup on each page.
	const onInteract = ( event ) => {
		if ( event.target instanceof Element && event.target.closest( '.gform_wrapper' ) ) {
			start();
		}
	};
	document.addEventListener( 'focusin', onInteract, { signal: controller.signal } );
	document.addEventListener( 'pointerdown', onInteract, { signal: controller.signal } );
}

export default function initDeferredMaps() {
	if ( ! document.querySelector( PLACEHOLDER ) ) {
		return;
	}

	watchForms();
}

// After load, so Maps never competes with the first paint.
if ( document.readyState === 'complete' ) {
	initDeferredMaps();
} else {
	window.addEventListener( 'load', initDeferredMaps, { once: true } );
}
